import { Icons } from '@/presentation/components/elements/Icon';
import Icon from '@/presentation/components/elements/Icon';
import LazyImage from '@/presentation/components/elements/LazyImage';
import Price from '@/presentation/components/elements/Price';
import { Address, addressEqual } from 'nftx.js';
import { useTranslations } from 'use-intl';
import { ReactNode, useState } from 'react';

const itemStyles =
  'w-full flex items-center hover:bg-gray-200 px-4 py-1.5 text-gray-800';
const selectedStyles = 'opacity-50 cursor-default';

export interface CollectionProps {
  id: Address;
  token: Address | '';
  totalHoldings: number;
  vaultIds: string[];
  name: string;
  imageUrl?: string;
  onClick: (vaultId: string) => void;
  bestPrice?: bigint;
  type: 'buy' | 'sell';
  collectionVaults: ReactNode;
}

export const Collection = ({
  id,
  token,
  name,
  imageUrl,
  totalHoldings,
  vaultIds,
  onClick,
  bestPrice,
  type,
  collectionVaults,
}: CollectionProps) => {
  const [showVaults, setShowVaults] = useState(false);
  const hasMultipleVaults = vaultIds.length > 1;
  const t = useTranslations('Swap.Search.Collection');
  const selected = addressEqual(token, id);

  return (
    <li>
      <button
        type="button"
        className={`${itemStyles} ${selected ? selectedStyles : ''}`}
        onClick={
          hasMultipleVaults
            ? () => setShowVaults((x) => !x)
            : () => onClick(vaultIds[0])
        }
      >
        <LazyImage
          src={imageUrl}
          className="w-10 h-10 flex-none mr-2 rounded-sm"
          alt={name}
        />
        <div className="flex flex-col items-start">
          <h4 className="">{name}</h4>
          <div className="text-sm text-gray-500 flex items-center">
            {type === 'sell'
              ? bestPrice != null
                ? t.rich('price', {
                    vaults: vaultIds.length,
                    price: (
                      <Price
                        key="bestPriceETH"
                        currency="ETH"
                        value={bestPrice}
                        maximumSignificantDigits={4}
                        minimumSignificantDigits={2}
                      />
                    ) as any,
                  })
                : null
              : totalHoldings}
            {vaultIds.length > 1 && (
              <>
                <span className="bg-gray-400 mx-1.5 h-1 w-1 rounded-full" />
                {t('vaults', { count: vaultIds.length })}
              </>
            )}
          </div>
        </div>
        <div className="ml-auto inline-flex">
          {type === 'buy'
            ? bestPrice != null
              ? t.rich('price', {
                  vaults: vaultIds.length,
                  price: (
                    <div className="ml-1" key={id}>
                      <Price
                        key="bestPriceETH"
                        currency="ETH"
                        value={bestPrice}
                        maximumSignificantDigits={4}
                        minimumSignificantDigits={2}
                      />
                      <div className="text-xxs text-right text-gray-500">
                        <Price
                          key="bestPriceUSD"
                          currency="USD"
                          value={bestPrice}
                          maximumFractionDigits={0}
                        />
                      </div>
                    </div>
                  ) as any,
                })
              : null
            : totalHoldings}
        </div>
        {selected && (
          <Icon name={Icons.CHECK} size={20} className="ml-2 text-pink-500" />
        )}
        {hasMultipleVaults && (
          <Icon
            name={Icons.CHEVRON_DOWN}
            size={20}
            className={`ml-2 text-gray-500 transform transition-transform ${
              showVaults ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>
      {showVaults && <ul className="p-1 bg-gray-200/50">{collectionVaults}</ul>}
    </li>
  );
};

export default Collection;
